<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  isToday,
  isCurrentMonth,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import CalendarEventChip from 'dashboard/components-next/Calendar/CalendarEventChip.vue';

const props = defineProps({
  weeks: { type: Array, required: true },
  currentDate: { type: Date, required: true },
  eventsForDay: { type: Function, required: true },
});

defineEmits(['reschedule', 'complete']);

const { t } = useI18n();

const WEEKDAY_LABELS = [
  'CALENDAR.WEEKDAYS.MON',
  'CALENDAR.WEEKDAYS.TUE',
  'CALENDAR.WEEKDAYS.WED',
  'CALENDAR.WEEKDAYS.THU',
  'CALENDAR.WEEKDAYS.FRI',
  'CALENDAR.WEEKDAYS.SAT',
  'CALENDAR.WEEKDAYS.SUN',
];

const MAX_VISIBLE_EVENTS = 3;

const today = computed(() => new Date());

const dayCellClass = day => ({
  'bg-n-alpha-1': !isCurrentMonth(day, props.currentDate),
  'ring-1 ring-n-brand ring-inset': isToday(today.value, day),
});

// Computed once per render instead of calling eventsForDay(day) multiple
// times per cell (slice + overflow-count) across up to 42 cells.
const weeksWithEvents = computed(() =>
  props.weeks.map(week =>
    week.map(day => ({ day, events: props.eventsForDay(day) }))
  )
);
</script>

<template>
  <div
    class="flex flex-col flex-1 min-h-0 overflow-hidden border rounded-lg border-n-weak"
  >
    <div class="grid flex-shrink-0 grid-cols-7 border-b border-n-weak">
      <div
        v-for="label in WEEKDAY_LABELS"
        :key="label"
        class="py-2 text-xs font-medium text-center text-n-slate-10"
      >
        {{ t(label) }}
      </div>
    </div>
    <div class="grid flex-1 grid-cols-7 auto-rows-fr">
      <div
        v-for="week in weeksWithEvents"
        :key="week[0].day.getTime()"
        class="contents"
      >
        <div
          v-for="cell in week"
          :key="cell.day.getTime()"
          class="flex flex-col min-h-[96px] gap-1 p-1.5 border-b border-r border-n-weak last:border-r-0"
          :class="dayCellClass(cell.day)"
        >
          <span
            class="text-xs font-medium"
            :class="
              isCurrentMonth(cell.day, currentDate)
                ? 'text-n-slate-11'
                : 'text-n-slate-8'
            "
          >
            {{ cell.day.getDate() }}
          </span>
          <div class="flex flex-col gap-0.5 overflow-hidden">
            <CalendarEventChip
              v-for="event in cell.events.slice(0, MAX_VISIBLE_EVENTS)"
              :key="event.contact.id"
              :event="event"
              compact
              @reschedule="$emit('reschedule', $event)"
              @complete="$emit('complete', $event)"
            />
            <span
              v-if="cell.events.length > MAX_VISIBLE_EVENTS"
              class="px-1.5 text-[11px] text-n-slate-9"
            >
              {{
                t('CALENDAR.MORE_EVENTS', {
                  count: cell.events.length - MAX_VISIBLE_EVENTS,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
